import React, { useState, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import Loader from '../../Loader';
import Toast from '../../Toast';
import useSuccessCriteria from '../../../hooks/useSuccessCriteria';
import ItemSettingsModal from '../../Shared/ItemSettingsModal';
import SettingsTagsDisplay from '../../Shared/SettingsTagsDisplay';
import { useProfile } from '../../state/hooks';
import { useUserStates } from '../../../hooks/useUserStates';
import useSettingsFilters from '../../../hooks/useSettingsFilters';

import ImprovedSecondarySideBar from '../../ImprovedSecondarySideBar';
import MenuButton from '../../MenuButton';
import ScrollableArea from '../../Shared/ScrollableArea';
import { isTabletView } from '../../ReactiveRender';


import editIcon from '../../../static/img/markEdit.svg';
import EditHover from '../../../static/img/EditHover.svg';
import plusIcon from '../../../static/img/Plus-violet.svg';

const MainContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100vh;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const RightContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: #ffffff;
  overflow: hidden;
  min-height: 0;
`;

const ContentContainer = styled(ScrollableArea).attrs({ autoHide: 'leave' })`
  flex: 1;
  padding: 24px 0 60px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  min-height: 0;
  box-sizing: border-box;
`;

const TitleSection = styled.div`
  padding: 0 24px;
`;

const MainContentSection = styled.div`
  padding: 0 24px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  margin-right: 8px;
  cursor: ${props => props.canEdit ? 'pointer' : 'default'};
  min-width: 0;
  flex: 1;
  
  &:hover {
    opacity: ${props => props.canEdit ? '0.8' : '1'};
  }
`;

const Title = styled.h1`
  font-family: IBM Plex Sans;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  color: #1e252a;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TitleInput = styled.input`
  font-family: IBM Plex Sans;
  font-size: 24px;
  font-weight: 600;
  outline: none;
  background: transparent;
  margin: 0;
  border: none;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #1e252a;
`;

const EditIconStyled = styled.img`
  width: 20px;
  height: 20px;
  opacity: 0.85;
  flex-shrink: 0;
  display: block;

  ${TitleContainer}:hover & {
    display: none;
  }
`;

const EditIconHover = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: none;

  ${TitleContainer}:hover & {
    display: block;
  }
`;

const EditIconContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const HeadingAndFilterContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #C9C6CC80;
`;

const SectionTitle = styled.h2`
  font-family: IBM Plex Sans;
  font-size: 18px;
  font-weight: 500;
  margin: 0 0 16px 0;
  color: #1e252a;
`;

const ObjectivesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 24px;
`;

const ObjectiveItem = styled.div`
  font-family: IBM Plex Sans;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #1e252a;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  position: relative;
  
  ${props => props.isSelected && `
    background-color: #F5F5F5;
    border-radius: 4px;
    padding: 10px 8px;
    margin: -2px;
  `}
  
  &:last-child {
    border-bottom: none;
  }
`;

const ObjectiveText = styled.span`
  display: block;
  flex: 1;
  padding-right: 40px;
`;

const ObjectiveContentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  position: relative;
  cursor: ${props => props.canEdit ? 'pointer' : 'default'};
`;

const ObjectiveEditIcon = styled.button`
  position: absolute;
  right: 32px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  border-radius: 4px;
  opacity: 0.35;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  ${ObjectiveItem}:hover & {
    opacity: 1;
  }

  &:hover {
    background-color: #f0f0f0;
  }

  img {
    width: 22px;
    height: 22px;
  }
`;

const ObjectiveInput = styled.input`
  font-family: IBM Plex Sans;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #1e252a;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  width: 100%;
  outline: none;
  background: transparent;
  
  &:focus {
    border: none;
    box-shadow: none;
  }
`;

const AddObjectiveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
  border: none;
  background-color: transparent;
  cursor: pointer;
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 500;
  color: #7200E0;
  transition: opacity 0.2s;
  margin-top: 8px;
  margin-bottom: 0;
  width: 100%;

  &:hover {
    opacity: 0.7;
  }

  img {
    width: 16px;
    height: 16px;
  }
`;

const DeleteButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #F44336;
  font-size: 18px;

  ${ObjectiveItem}:hover & {
    opacity: 1;
  }

  &:hover {
    background-color: #f0f0f0;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  min-width: 0;
  width: 100%;
`;

const BackArrow = styled.span`
  font-family: IBM Plex Sans;
  font-size: 20px;
  font-weight: 400;
  color: #7200E0;
  cursor: pointer;
  padding: 4px 8px;
  flex-shrink: 0;
  &:hover {
    opacity: 0.8;
  }
`;

const DetailsStrip = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 24px;
  margin: 4px 0 24px 0;
  background: #F5F0FF;
  border-radius: 0;
  border: 1px solid #E8E0F5;
  box-sizing: border-box;
  overflow: hidden;
  isolation: isolate;
`;

const SuccessCriteriaDetail = () => {
  const { successCriteriaId } = useParams();
  const history = useHistory();
  const { data: profile } = useProfile();
  const { states: userStates } = useUserStates();
  const userState = userStates[0] || null;

  const {
    successCriteria,
    isLoading,
    updateSuccessCriteriaTitle,
    addObjective,
    updateObjective,
    deleteObjective
  } = useSuccessCriteria(successCriteriaId);

  // Get label mappings for displaying current settings
  const { taskTypeLabels, responseTypeLabels } = useSettingsFilters({
    state: userState,
    initialYears: successCriteria?.years || [],
    initialSubjects: (successCriteria?.metadata?.subjects || []).map((s) =>
      typeof s === 'object' ? (s.subjectCode || s.code || s) : s
    ),
    initialTaskTypes: successCriteria?.taskTypes || [],
    initialResponseTypes: successCriteria?.responseTypes || [],
    enableYearFilter: false,
  });

  const [isShowMenu, setShowMenu] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editingObjective, setEditingObjective] = useState(null);
  const editingObjectiveRef = useRef(null);
  const selectObjectiveEditor = next => {
    editingObjectiveRef.current = next;
    setEditingObjective(next);
  };
  const [editingObjectiveText, setEditingObjectiveText] = useState('');
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [isNewObjective, setIsNewObjective] = useState(false);
  const [showSettingsPopup, setShowSettingsPopup] = useState(false);
  const tabletView = isTabletView();
  const contentContainerRef = useRef(null);
  const objectiveRefs = useRef({});
  const addButtonRef = useRef(null);

  React.useEffect(() => {
    selectObjectiveEditor(null);
    setEditingObjectiveText('');
    setSelectedObjective(null);
    setIsNewObjective(false);
    setIsEditingTitle(false);
    setShowSettingsPopup(false);
  }, [successCriteriaId]);

  const handleTitleClick = () => {
    if (!successCriteria || successCriteria.isSystem) return;
    setIsEditingTitle(true);
    setEditTitle(successCriteria.title);
  };

  const handleTitleChange = (event) => {
    setEditTitle(event.target.value);
  };

  const handleTitleKeyPress = (event) => {
    if (event.key === 'Enter') {
      saveTitle();
    }
  };

  const handleTitleBlur = () => {
    saveTitle();
  };

  const saveTitle = async () => {
    if (isEditingTitle) {
      if (!editTitle.trim() || editTitle === successCriteria.title) {
        setIsEditingTitle(false);
        return;
      }

      try {
        await updateSuccessCriteriaTitle({ title: editTitle.trim() });
      } catch (error) {
        console.error('Error updating success criteria title:', error);
      }
      
      setIsEditingTitle(false);
    }
  };

  const handleObjectiveEditClick = (criteriaIndex, objectiveIndex, objective) => {
    if (!successCriteria || successCriteria.isSystem) return;
    setSelectedObjective({ criteriaIndex, objectiveIndex });
    selectObjectiveEditor({ criteriaIndex, objectiveIndex });
    setEditingObjectiveText(objective.description || '');
    setIsNewObjective(false);
  };

  const handleObjectiveBlur = async (criteriaIndex, objectiveIndex) => {
    const owner = editingObjectiveRef.current;
    if (!owner || owner.criteriaIndex !== criteriaIndex || owner.objectiveIndex !== objectiveIndex) return;

    if (!editingObjectiveText.trim()) {
      if (isNewObjective) {
        try {
          await deleteObjective(criteriaIndex, objectiveIndex, true);
        } catch (error) {
          console.error('Error removing empty objective:', error);
          return;
        }
      } else {
        try {
          await deleteObjective(criteriaIndex, objectiveIndex);
        } catch (error) {
          console.error('Error deleting objective:', error);
          toast(<Toast message="The objective could not be saved. Please try again." />);
          return;
        }
      }
    } else {
      try {
        await updateObjective(criteriaIndex, objectiveIndex, {
          description: editingObjectiveText.trim()
        });
      } catch (error) {
        console.error('Error updating objective:', error);
        toast(<Toast message="The objective could not be saved. Please try again." />);
        return;
      }
    }

    if (editingObjectiveRef.current !== owner) return;
    selectObjectiveEditor(null);
    setEditingObjectiveText('');
    setSelectedObjective(null);
    setIsNewObjective(false);
  };

  const handleObjectiveKeyPress = (event, criteriaIndex, objectiveIndex) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleObjectiveBlur(criteriaIndex, objectiveIndex);
    }
  };

  const handleDeleteObjective = async (criteriaIndex, objectiveIndex) => {
    try {
      await deleteObjective(criteriaIndex, objectiveIndex);
    } catch (error) {
      console.error('Error deleting objective:', error);
    }
  };

  const handleAddObjective = async (criteriaIndex) => {
    const newObjective = {
      description: ''
    };

    try {
      const newIndex = await addObjective(criteriaIndex, newObjective, true);
      selectObjectiveEditor({ criteriaIndex, objectiveIndex: newIndex });
      setEditingObjectiveText('');
      setSelectedObjective({ criteriaIndex, objectiveIndex: newIndex });
      setIsNewObjective(true);

      setTimeout(() => {
        const objectiveKey = `${criteriaIndex}-${newIndex}`;
        const objectiveElement = objectiveRefs.current[objectiveKey];
        if (objectiveElement) {
          objectiveElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      }, 150);
    } catch (error) {
      console.error('Error adding objective:', error);
    }
  };

  const handleSettingsSave = async (settings) => {
    try {
      await updateSuccessCriteriaTitle({
        years: settings.years || [],
        subjects: settings.subjects || [],
        taskTypes: settings.taskTypes || (settings.taskType ? [settings.taskType] : []),
        responseTypes: settings.responseTypes || (settings.responseType ? [settings.responseType] : []),
      });
      toast(<Toast message="Settings saved successfully" />);
    } catch (error) {
      console.error('Error updating settings:', error);
      toast(<Toast message="Failed to save settings" />);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  if (!successCriteria) {
    return (
      <div>
        Success criteria not found
      </div>
    );
  }

  const canEdit = !successCriteria.isSystem;
  
  const currentCriteria = successCriteria.criterias && successCriteria.criterias.length > 0 
    ? successCriteria.criterias[0] 
    : { objectives: [] };
  const criteriaIndex = 0;

  return (
    <MainContainer>
            <ImprovedSecondarySideBar
        isShowMenu={isShowMenu}
        setShowMenu={setShowMenu}
        id={successCriteriaId}
      />
      <InnerContainer>
        {tabletView && (
          <HeadingAndFilterContainer>
            <MenuButton setShowMenu={setShowMenu} />
          </HeadingAndFilterContainer>
        )}
         
        <RightContainer>
          <ContentContainer ref={contentContainerRef}>
            <TitleSection>
            <TitleRow>
              <BackArrow onClick={() => history.push('/tools/successcriterias')}>&lt;</BackArrow>
              <TitleContainer onClick={handleTitleClick} canEdit={canEdit}>
                {isEditingTitle ? (
                  <TitleInput
                    value={editTitle}
                    onChange={handleTitleChange}
                    onBlur={handleTitleBlur}
                    onKeyPress={handleTitleKeyPress}
                    autoFocus
                  />
                ) : (
                  <>
                    <Title>{successCriteria.title}</Title>
                    {canEdit && (
                      <EditIconContainer>
                        <EditIconStyled src={editIcon} alt="Edit title" />
                        <EditIconHover src={EditHover} alt="Edit title" />
                      </EditIconContainer>
                    )}
                  </>
                )}
              </TitleContainer>
            </TitleRow>
            </TitleSection>
            <DetailsStrip>
              <SettingsTagsDisplay
                years={successCriteria.years || (successCriteria.year ? [successCriteria.year] : [])}
                subjects={successCriteria.metadata?.subjects?.length ? successCriteria.metadata.subjects : (successCriteria.subjects || [])}
                taskTypes={successCriteria.taskTypes || (successCriteria.taskType ? [successCriteria.taskType] : [])}
                responseTypes={successCriteria.responseTypes || (successCriteria.responseType ? [successCriteria.responseType] : [])}
                taskTypeLabels={taskTypeLabels}
                responseTypeLabels={responseTypeLabels}
                onSettingsClick={canEdit ? () => setShowSettingsPopup(true) : undefined}
                showSettingsButton={canEdit}
              />
            </DetailsStrip>

            <MainContentSection>
            <SectionTitle>Objectives</SectionTitle>
            
            <ObjectivesList>
              {currentCriteria.objectives && currentCriteria.objectives.length > 0 ? (
                currentCriteria.objectives.map((objective, objectiveIndex) => {
                  const isEditing = editingObjective?.criteriaIndex === criteriaIndex && 
                                   editingObjective?.objectiveIndex === objectiveIndex;
                  const isSelected = selectedObjective?.criteriaIndex === criteriaIndex && 
                                   selectedObjective?.objectiveIndex === objectiveIndex;
                  
                  return (
                    <ObjectiveItem 
                      key={objectiveIndex} 
                      isSelected={isSelected}
                      ref={(el) => {
                        objectiveRefs.current[`${criteriaIndex}-${objectiveIndex}`] = el;
                      }}
                    >
                      {isEditing ? (
                        <ObjectiveInput
                          type="text"
                          value={editingObjectiveText}
                          onChange={(e) => setEditingObjectiveText(e.target.value)}
                          onBlur={() => handleObjectiveBlur(criteriaIndex, objectiveIndex)}
                          onKeyDown={(e) => handleObjectiveKeyPress(e, criteriaIndex, objectiveIndex)}
                          autoFocus
                        />
                      ) : (
                        <>
                          <ObjectiveContentWrapper
                            canEdit={canEdit}
                            onDoubleClick={() => canEdit && handleObjectiveEditClick(criteriaIndex, objectiveIndex, objective)}
                          >
                            <ObjectiveText>
                              {objective.description}
                            </ObjectiveText>
                            {canEdit && (
                              <ObjectiveEditIcon
                                onClick={() => handleObjectiveEditClick(criteriaIndex, objectiveIndex, objective)}
                                aria-label="Edit objective"
                              >
                                <img src={editIcon} alt="Edit" />
                              </ObjectiveEditIcon>
                            )}
                          </ObjectiveContentWrapper>
                          {canEdit && (
                            <DeleteButton
                              onClick={() => handleDeleteObjective(criteriaIndex, objectiveIndex)}
                              aria-label="Delete objective"
                            >
                              ×
                            </DeleteButton>
                          )}
                        </>
                      )}
                    </ObjectiveItem>
                  );
                })
              ) : (
                <div style={{ 
                  padding: '24px', 
                  textAlign: 'center', 
                  color: '#7b7382',
                  fontFamily: 'IBM Plex Sans',
                  fontSize: '14px'
                }}>
                  No objectives added yet. Click "Add New Objective" to get started.
                </div>
              )}
            </ObjectivesList>
            
            {canEdit && (
              <AddObjectiveButton 
                ref={addButtonRef}
                onClick={() => handleAddObjective(criteriaIndex)}
              >
                <img src={plusIcon} alt="Add" />
                Add New Objective
              </AddObjectiveButton>
            )}
            </MainContentSection>
          </ContentContainer>
        </RightContainer>
      </InnerContainer>

      <ItemSettingsModal
        open={showSettingsPopup}
        onClose={() => setShowSettingsPopup(false)}
        item={successCriteria}
        state={userState}
        onSave={handleSettingsSave}
      />
    </MainContainer>
  );
};

export default SuccessCriteriaDetail;
